import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Grid, Box, Card, CardHeader, CardContent } from "@material-ui/core";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";

import httpClient from "../../lib/httpClient";
import { DataGridCystellarTable } from "../util/DataGridCystellarTable";
import {
  addExportButton,
  numberFilterOperators,
  tableCellRender,
  fromStringToNumberParser,
} from "../util/tableUtils";

export const ClaimsKpi = () => {
  const { t } = useTranslation();

  const [claimsDataFetching, setClaimsDataFetching] = useState(false);
  const [claimsData, setClaimsData] = useState([]);
  const [plotData, setPlotData] = useState([]);
  const [claimsUpdateDate, setClaimsUpdateDate] = useState("");

  const getClaimsData = async () => {
    const [{ data: claimsResult }, { data: claimsUpdateResult }] = await Promise.all([
      httpClient().get(`/kpi/claims`),
      httpClient().get(`/kpi/claimsUpdateDateStamp`),
    ]);

    const dataWithId = claimsResult.map((item, index) => ({ id: index, ...item }));
    setClaimsData(dataWithId);
    setClaimsDataFetching(false);

    const usersUpdateDate = claimsUpdateResult.filter((item) => item.Title === "updated")[0].Value;
    setClaimsUpdateDate(usersUpdateDate);
  };

  useEffect(() => {
    setClaimsDataFetching(true);
    getClaimsData();
  }, []);

  useEffect(() => {
    const claimsWithoutTotal = claimsData.map(({ claims_ita, claims_rma, ...rest }) => ({ ...rest }));
    setPlotData(claimsWithoutTotal);
  }, [claimsData]);

  const claimsColumns = [
    {
      field: "year",
      headerName: "Year",
    },
    {
      field: "month",
      headerName: "Month",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "from_date",
      headerName: "From Date",
      sortable: false,
      filterable: false,
    },
    {
      field: "to_date",
      headerName: "To Date",
      sortable: false,
      filterable: false,
    },
    {
      field: "missingPolicies_ita",
      headerName: "ITA Missing Policies",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "has_address_ita",
      headerName: "ITA Has Address",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "missing_address_ita",
      headerName: "ITA Missing Address",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "claims_ita",
      headerName: "ITA Claims",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "missingPolicies_rma",
      headerName: "RMA Missing Policies",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "has_address_rma",
      headerName: "RMA Has Address",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "missing_address_rma",
      headerName: "RMA Missing Address",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
    {
      field: "claims_rma",
      headerName: "RMA Claims",
      valueParser: fromStringToNumberParser,
      filterOperators: numberFilterOperators,
    },
  ];

  const columns = claimsColumns.map((col) => ({
    ...col,
    flex: 1,
    valueFormatter: (params) => (col.field !== "year" && tableCellRender(params.value)) || params.value,
    align: "right",
    headerAlign: "right",
  }));

  return (
    <Card>
      <CardHeader title={"Claims KPI"} />
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={10}>
            <Box>
              <DataGridCystellarTable
                rows={claimsData}
                columns={columns}
                getRowId={(rowData) => rowData.id}
                loading={claimsDataFetching}
                pagination={false}
                customToolbarComp={true}
                customToolbarWidgets={addExportButton("Claims KPI")}
                customToolbarCompMsg={`${t("policy.databaseLastUpdate")} ${claimsUpdateDate}. ${t(
                  "policy.updatedDaily",
                )} `}
              />
            </Box>
          </Grid>

          <Grid item xs={12} sm={8}>
            <Box p={1}>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart
                  width={600}
                  height={300}
                  data={plotData}
                  margin={{
                    top: 20,
                    right: 30,
                    left: 20,
                    bottom: 5,
                  }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="missingPolicies_ita" stackId="a" fill="#FFA500" />
                  <Bar dataKey="has_address_ita" stackId="a" fill="#d5b9da" />
                  <Bar dataKey="missing_address_ita" stackId="a" fill="#6d9aad" />

                  <Bar dataKey="missingPolicies_rma" stackId="b" fill="#FFA500" />
                  <Bar dataKey="has_address_rma" stackId="b" fill="#d5b9da" />
                  <Bar dataKey="missing_address_rma" stackId="b" fill="#6d9aad" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};
